/**
 * MandateOutcome — Day 100 Outcome hypothesis and version history.
 */

import { useState, useEffect, useRef } from 'react';
import { Target } from 'lucide-react';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { reflectOnHypothesis } from '../../lib/llmMandateService';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function MandateOutcome() {
  const { transition, currentOutcome, outcomeVersions, setOutcomeHypothesis } = useMandateStore();

  const [editing, setEditing] = useState(false);
  const [newHypothesis, setNewHypothesis] = useState('');
  const [revisionReason, setRevisionReason] = useState('');
  const [saving, setSaving] = useState(false);

  const [reflection, setReflection] = useState<string | null>(null);
  const [reflecting, setReflecting] = useState(false);
  const reflectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced AI reflection while revising the hypothesis
  useEffect(() => {
    if (!editing) return;
    setReflection(null);
    if (reflectTimer.current) clearTimeout(reflectTimer.current);
    if (newHypothesis.trim().length < 40) return;

    setReflecting(true);
    reflectTimer.current = setTimeout(async () => {
      try {
        const result = await reflectOnHypothesis(newHypothesis, transition?.situationType);
        setReflection(result);
      } finally {
        setReflecting(false);
      }
    }, 1500);

    return () => {
      if (reflectTimer.current) clearTimeout(reflectTimer.current);
    };
  }, [newHypothesis, editing, transition?.situationType]);

  function openEdit() {
    setNewHypothesis(currentOutcome?.hypothesis ?? '');
    setRevisionReason('');
    setReflection(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setNewHypothesis('');
    setRevisionReason('');
    setReflection(null);
  }

  async function handleSave() {
    if (!newHypothesis.trim() || !revisionReason.trim()) return;
    setSaving(true);
    try {
      await setOutcomeHypothesis(newHypothesis.trim(), revisionReason.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  const sortedVersions = [...outcomeVersions].sort((a, b) => b.version - a.version);

  return (
    <div>
      <PageHeader
        icon={<Target className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Day 100 Outcome"
        subtitle="Your hypothesis for what success looks like"
        actions={
          !editing && currentOutcome ? (
            <button
              onClick={openEdit}
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 text-sm"
            >
              Revise →
            </button>
          ) : undefined
        }
      />

      {/* Current hypothesis card */}
      {currentOutcome ? (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 border-l-4 border-l-indigo-400 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
              Current Hypothesis
            </span>
            {currentOutcome.version > 1 && (
              <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium px-1.5 py-0.5 rounded">
                v{currentOutcome.version}
              </span>
            )}
          </div>
          <p className="text-lg text-[var(--text-pri)] leading-relaxed">{currentOutcome.hypothesis}</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-4">
          <p className="text-[var(--text-ter)] text-sm">No hypothesis set yet.</p>
          {!editing && (
            <button
              onClick={openEdit}
              className="mt-3 bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 text-sm"
            >
              Set hypothesis →
            </button>
          )}
        </div>
      )}

      {/* Revise panel */}
      {editing && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-4">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-4">Revise hypothesis</h3>
          <div className="mb-3">
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">New hypothesis</label>
            <textarea
              value={newHypothesis}
              onChange={(e) => setNewHypothesis(e.target.value)}
              rows={5}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* AI Reflection */}
          {(reflecting || reflection) && (
            <div
              className={`mb-4  border px-4 py-3 transition-all ${
                reflecting ? 'border-[var(--rule)] bg-[var(--surface)]' : 'border-[var(--slate)] bg-[var(--slate-dim)]'
              }`}
            >
              {reflecting ? (
                <p className="text-sm text-[var(--text-ter)] italic">Reflecting…</p>
              ) : (
                <p className="text-sm text-[var(--text-ter)] leading-relaxed">{reflection}</p>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              What changed? What did you learn that made you revise this?{' '}
              <span className="text-[var(--coral)]">*</span>
            </label>
            <textarea
              value={revisionReason}
              onChange={(e) => setRevisionReason(e.target.value)}
              rows={3}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              placeholder="Describe what new information or insight prompted this revision..."
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!newHypothesis.trim() || !revisionReason.trim() || saving}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2 text-sm"
            >
              {saving ? 'Saving...' : 'Save revision'}
            </button>
            <button
              onClick={cancelEdit}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Version history */}
      {sortedVersions.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
            Revision history
          </h2>
          <div className="space-y-3">
            {sortedVersions.map((v) => (
              <div key={v.id} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-[var(--text-ter)]">v{v.version}</span>
                  <span className="text-xs text-[var(--text-ter)]">{formatDate(v.createdAt)}</span>
                  {v.isCurrent && (
                    <span className="text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] font-medium px-1.5 py-0.5 rounded">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-sm text-[var(--text-sec)] mb-1">
                  {v.hypothesis.length > 100
                    ? `${v.hypothesis.slice(0, 100)}…`
                    : v.hypothesis}
                </p>
                {v.revisionReason && (
                  <p className="text-xs text-[var(--text-ter)] italic">
                    Changed because: {v.revisionReason}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
